/*
  إدارة المستخدمين وتغيير كلمات السر
  نظام متابعة الدوام - مراقبة التشغيل
*/
import React, { useEffect, useState } from 'react';

const API_URL = window.location.port === '3000' ? 'http://localhost:5000/api' : '/api';

const ROLE_LABELS = {
  admin: 'أدمن / Admin',
  manager: 'مراقب / Manager',
  editor: 'محرّر / Editor',
  shiftSupervisor: 'مسؤول نوبة / Shift Supervisor',
  employee: 'موظف / Employee',
  viewer: 'مشاهد / Viewer'
};

const PERMISSIONS = [
  ['employee', 'موظف'],
  ['shiftSupervisor', 'مسؤول نوبة'],
  ['manager', 'مراقب'],
  ['admin', 'أدمن'],
  ['editor', 'محرّر'],
  ['viewer', 'مشاهد']
];

const SHIFT_SCOPE_OPTIONS = ['A', 'B', 'C', 'D', 'F', 'M'];
const WORK_LOCATION_SCOPE_OPTIONS = ['TCR1', 'TCR2', 'TCR3', 'TCR4', 'ECR', 'ACR', 'Distillation', 'SWGT(08)', 'SWGT(24)', 'SWGT(CCGT-3)', 'SBGT(LM6000)', 'SBGT(FRAME 7)', 'SWPS', 'صباحي'];

function authFetch(url, options = {}) {
  const token = localStorage.getItem('opd_token') || '';
  return fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}), Authorization: `Bearer ${token}` }
  });
}

const inp = { width: '100%', padding: '10px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' };
const btn = { padding: '8px 16px', border: 'none', borderRadius: '5px', cursor: 'pointer', color: 'white', fontWeight: 'bold' };
const td = { padding: '9px', borderBottom: '1px solid #eee', verticalAlign: 'top' };
const th = { padding: '10px', textAlign: 'right', borderBottom: '2px solid #bdc3c7' };

function toggleArray(arr, value) {
  const list = Array.isArray(arr) ? arr : [];
  return list.includes(value) ? list.filter(x => x !== value) : [...list, value];
}


function toggleScopeValue(scope = {}, key, value) {
  const current = Array.isArray(scope[key]) ? scope[key] : [];
  return { ...scope, [key]: toggleArray(current, value) };
}

function scopeIsEmpty(scope) {
  const shifts = Array.isArray(scope?.shifts) ? scope.shifts.filter(Boolean) : [];
  const workplaces = Array.isArray(scope?.workplaces) ? scope.workplaces.filter(Boolean) : [];
  return shifts.length === 0 && workplaces.length === 0;
}

function permissionsText(perms) {
  const list = Array.isArray(perms) ? perms : [];
  return list.map(p => ROLE_LABELS[p] || p).join('، ') || '-';
}

// ====== إدارة المستخدمين (للأدمن والمراقب فقط) ======
export function UserManagement({ currentUser }) {
  const isEnglish = currentUser?.language === 'en';
  const permissions = Array.isArray(currentUser?.permissions) ? currentUser.permissions : [];
  const hasCurrentPermission = (permission) => permissions.includes(permission) || currentUser?.role === permission;
  const editorOnly = hasCurrentPermission('editor') && !hasCurrentPermission('admin') && !hasCurrentPermission('manager');
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [createdAccounts, setCreatedAccounts] = useState([]);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [form, setForm] = useState({
    employeeId: '',
    username: '',
    password: '',
    name: '',
    language: 'ar',
    permissions: ['employee']
  });

  const load = async () => {
    setLoading(true);
    setMsg('');
    try {
      const [usersRes, employeesRes] = await Promise.all([
        authFetch(`${API_URL}/users`),
        authFetch(`${API_URL}/employees`)
      ]);
      const usersData = await usersRes.json();
      const employeesData = await employeesRes.json();
      if (usersRes.ok) setUsers(usersData.users || []);
      else setMsg(usersData.error || 'تعذر تحميل المستخدمين');
      if (employeesRes.ok) setEmployees(employeesData.employees || []);
    } catch { setMsg('تعذر الاتصال بالخادم'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const selectEmployee = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    setForm(prev => ({
      ...prev,
      employeeId,
      username: emp?.fileNo || prev.username,
      name: emp?.fullName || prev.name,
      permissions: prev.permissions.includes('employee') ? prev.permissions : [...prev.permissions, 'employee']
    }));
  };

  const addUser = async (e) => {
    e.preventDefault();
    setMsg('');
    setCreatedAccounts([]);
    try {
      let res;
      if (form.employeeId) {
        res = await authFetch(`${API_URL}/employee/${form.employeeId}/create-user`, {
          method: 'POST',
          body: JSON.stringify({
            username: form.username,
            password: form.password || undefined,
            name: form.name,
            language: form.language,
            permissions: form.permissions
          })
        });
      } else {
        res = await authFetch(`${API_URL}/users`, { method: 'POST', body: JSON.stringify(form) });
      }
      const data = await res.json();
      if (res.ok) {
        setMsg('تم إنشاء المستخدم بنجاح ✅');
        if (data.account) setCreatedAccounts([data.account]);
        setForm({ employeeId: '', username: '', password: '', name: '', language: 'ar', permissions: ['employee'] });
        load();
      } else setMsg(data.error || 'تعذر الإنشاء');
    } catch { setMsg('تعذر الاتصال بالخادم'); }
  };

  const bulkCreate = async () => {
    if (!window.confirm('إنشاء حسابات لكل الموظفين الذين لا يملكون حساب؟')) return;
    setMsg('');
    setCreatedAccounts([]);
    try {
      const res = await authFetch(`${API_URL}/users/create-employee-accounts`, { method: 'POST', body: JSON.stringify({ language: 'ar' }) });
      const data = await res.json();
      if (res.ok) {
        setMsg(`${data.message} ✅`);
        setCreatedAccounts(data.created || []);
        load();
      } else setMsg(data.error || 'تعذر إنشاء الحسابات');
    } catch { setMsg('تعذر الاتصال بالخادم'); }
  };

  const updateLocalUser = (id, patch) => {
    setUsers(prev => prev.map(u => u.id === id ? { ...u, ...patch } : u));
  };

  const saveUser = async (u) => {
    const isSupervisor = (u.permissions || []).includes('shiftSupervisor');
    if (isSupervisor && scopeIsEmpty(u.supervisorScope)) {
      const proceed = window.confirm(
        'تنبيه: هذا المستخدم لديه صلاحية "مسؤول نوبة" لكن لم تحدد له أي نوبة أو مكان عمل ضمن النطاق.\n' +
        'سيظهر له شاشة الاعتماد، لكنه لن يستطيع اعتماد أو رفض أي طلب فعليًا.\n\n' +
        'اضغط "إلغاء" للرجوع وتحديد النطاق أولاً، أو "موافق" للحفظ كما هو رغم ذلك.'
      );
      if (!proceed) return;
    }
    const body = {
      permissions: Array.isArray(u.permissions) ? u.permissions : [],
      language: u.language || 'ar',
      active: u.active !== false,
      name: u.name || '',
      supervisorScope: u.supervisorScope || { shifts: [], workplaces: [] }
    };
    const res = await authFetch(`${API_URL}/users/${u.id}`, { method: 'PUT', body: JSON.stringify(body) });
    const data = await res.json();
    alert(res.ok ? 'تم حفظ الصلاحيات ✅' : (data.error || 'تعذر الحفظ'));
    load();
  };

  const openResetPassword = (id, username) => {
    setResetTarget({ id, username });
    setResetPasswordValue('');
    setResetPasswordConfirm('');
    setResetMessage('');
  };

  const closeResetPassword = () => {
    if (resetBusy) return;
    setResetTarget(null);
    setResetPasswordValue('');
    setResetPasswordConfirm('');
    setResetMessage('');
  };

  const submitResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    if (resetPasswordValue.length < 6) {
      setResetMessage(isEnglish ? 'Password must be at least 6 characters.' : 'كلمة المرور يجب ألا تقل عن 6 أحرف.');
      return;
    }
    if (resetPasswordValue !== resetPasswordConfirm) {
      setResetMessage(isEnglish ? 'Passwords do not match.' : 'كلمتا المرور غير متطابقتين.');
      return;
    }
    setResetBusy(true);
    try {
      const res = await authFetch(`${API_URL}/users/${resetTarget.id}/reset-password`, { method: 'POST', body: JSON.stringify({ newPassword: resetPasswordValue }) });
      const data = await res.json();
      if (res.ok) {
        setResetMessage(isEnglish ? 'Password reset successfully. Old sessions were closed. ✅' : 'تمت إعادة تعيين كلمة المرور وإغلاق الجلسات القديمة. ✅');
        setTimeout(() => closeResetPassword(), 1100);
      } else {
        setResetMessage(data.error || (isEnglish ? 'Unable to reset password.' : 'تعذر إعادة تعيين كلمة المرور.'));
      }
    } catch {
      setResetMessage(isEnglish ? 'Cannot connect to server.' : 'تعذر الاتصال بالخادم.');
    } finally {
      setResetBusy(false);
    }
  };

  const deleteUser = async (id, username) => {
    if (!window.confirm(`حذف المستخدم "${username}"؟`)) return;
    const res = await authFetch(`${API_URL}/users/${id}`, { method: 'DELETE' });
    const data = await res.json();
    if (!res.ok) alert(data.error || 'تعذر الحذف');
    load();
  };

  const filteredEmployees = employees.filter(emp => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) return true;
    return String(emp.fullName || '').toLowerCase().includes(q)
      || String(emp.fileNo || '').includes(q)
      || String(emp.civilID || '').includes(q)
      || String(emp.workLocation || '').toLowerCase().includes(q);
  }).slice(0, 120);

  return (
    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px' }}>
      <h2 style={{ marginTop: 0, color: '#2c3e50' }}>{editorOnly ? (isEnglish ? 'Reset User Passwords' : 'إعادة تعيين كلمات مرور المستخدمين') : (isEnglish ? 'Users and Permissions Management' : 'إدارة المستخدمين والصلاحيات')}</h2>
      <p style={{ color: '#555', marginTop: 0 }}>{editorOnly ? (isEnglish ? 'Editors can reset a forgotten password only. They cannot change permissions, disable accounts, create users, or delete users.' : 'يستطيع المحرر إعادة تعيين كلمة المرور المنسية فقط، ولا يستطيع تعديل الصلاحيات أو إيقاف الحساب أو إنشاء مستخدم أو حذفه.') : 'كل موظف يمكن أن يكون له حساب دخول، ويمكن إضافة صلاحيات إضافية مثل مسؤول نوبة أو مراقب أو أدمن.'}</p>

      {!editorOnly && <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '25px' }}>
        <h3 style={{ marginTop: 0 }}>إضافة مستخدم / إنشاء حساب موظف</h3>
        <form onSubmit={addUser}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px' }}>
            <div>
              <input
                type="text"
                placeholder="ابحث عن الموظف بالاسم أو رقم الملف أو الرقم المدني"
                value={employeeSearch}
                onChange={(e) => setEmployeeSearch(e.target.value)}
                style={inp}
              />
              <select value={form.employeeId} onChange={(e) => selectEmployee(e.target.value)} style={inp}>
                <option value="">مستخدم غير مربوط بموظف</option>
                {filteredEmployees.map(emp => <option key={emp.id} value={emp.id}>{emp.fullName} - ملف {emp.fileNo} - {emp.shift || '-'}</option>)}
              </select>
              <small style={{ color: '#666' }}>اكتب جزء من الاسم أو رقم الملف ثم اختر الموظف من القائمة.</small>
            </div>
            <input type="text" placeholder="اسم المستخدم" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} style={inp} required />
            <input type="text" placeholder="الاسم المعروض" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inp} />
            <input type="text" placeholder="كلمة المرور المؤقتة (اتركها فارغة للتوليد)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} style={inp} />
            <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} style={inp}>
              <option value="ar">عربي</option>
              <option value="en">English</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '8px 0 16px' }}>
            {PERMISSIONS.map(([key, label]) => (
              <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'white', border: '1px solid #ddd', padding: '8px 10px', borderRadius: '6px' }}>
                <input type="checkbox" checked={form.permissions.includes(key)} onChange={() => setForm({ ...form, permissions: toggleArray(form.permissions, key) })} />
                {label}
              </label>
            ))}
          </div>
          <button type="submit" style={{ ...btn, backgroundColor: '#27ae60' }}>إضافة المستخدم</button>
          <button type="button" onClick={bulkCreate} style={{ ...btn, backgroundColor: '#2980b9', marginRight: '10px' }}>إنشاء حسابات جميع الموظفين</button>
          {msg && <span style={{ marginRight: '12px', color: msg.includes('✅') ? '#27ae60' : '#e74c3c', fontWeight: 'bold' }}>{msg}</span>}
        </form>
      </div>}

      {!editorOnly && createdAccounts.length > 0 && (
        <div style={{ backgroundColor: '#fff9e6', border: '1px solid #f1c40f', padding: '15px', borderRadius: '8px', marginBottom: '25px' }}>
          <h3 style={{ marginTop: 0 }}>كلمات المرور المؤقتة</h3>
          <p style={{ color: '#7f6000' }}>هذه تظهر الآن فقط. اطبعها أو انسخها قبل مغادرة الصفحة.</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['الموظف', 'اسم المستخدم', 'كلمة المرور المؤقتة', 'الصلاحيات'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {createdAccounts.map((a, idx) => (
                <tr key={idx}>
                  <td style={td}>{a.employeeName || '-'}</td>
                  <td style={{ ...td, fontWeight: 'bold' }}>{a.username}</td>
                  <td style={{ ...td, fontWeight: 'bold', direction: 'ltr' }}>{a.temporaryPassword}</td>
                  <td style={td}>{permissionsText(a.permissions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading ? <p style={{ textAlign: 'center', padding: '20px' }}>جاري التحميل...</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#ecf0f1' }}>
              {(editorOnly ? ['اسم المستخدم', 'الموظف المرتبط', 'الصلاحيات', 'الحالة', 'إجراء'] : ['اسم المستخدم', 'الموظف المرتبط', 'الصلاحيات', 'نطاق مسؤول النوبة', 'اللغة', 'الحالة', 'إجراءات']).map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={{ ...td, fontWeight: 'bold' }}>{u.username}<br /><small>{u.name || '-'}</small></td>
                <td style={td}>{u.employeeName || '-'}<br /><small>{u.fileNo ? `ملف: ${u.fileNo}` : ''} {u.shift ? ` / نوبة: ${u.shift}` : ''}</small></td>
                <td style={td}>
                  {editorOnly ? permissionsText(u.permissions) : <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {PERMISSIONS.map(([key, label]) => (
                      <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                        <input
                          type="checkbox"
                          disabled={u.username === 'OPD'}
                          checked={(u.permissions || []).includes(key)}
                          onChange={() => updateLocalUser(u.id, { permissions: toggleArray(u.permissions, key) })}
                        />
                        {label}
                      </label>
                    ))}
                  </div>}
                </td>
                {!editorOnly && <td style={td}>
                  {(u.permissions || []).includes('shiftSupervisor') ? (
                    <div style={{ minWidth: 260 }}>
                      <strong style={{ display: 'block', marginBottom: 6 }}>نطاق مسؤول النوبة</strong>
                      {scopeIsEmpty(u.supervisorScope) && (
                        <div style={{ background: '#fdecea', border: '1px solid #e74c3c', color: '#c0392b', borderRadius: 4, padding: '6px 8px', fontSize: 12, marginBottom: 8, fontWeight: 'bold' }}>
                          ⚠️ لم يتم تحديد نوبة أو مكان عمل — لن يستطيع هذا المستخدم اعتماد أو رفض أي طلب رغم ظهور الصفحة له. حدد نوبة واحدة على الأقل أو مكان عمل واحد ثم اضغط حفظ.
                        </div>
                      )}
                      <div style={{ marginBottom: 6 }}>النوبة:</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                        {SHIFT_SCOPE_OPTIONS.map(x => <label key={x} style={{ fontSize: 12 }}><input type="checkbox" checked={((u.supervisorScope?.shifts) || []).includes(x)} onChange={() => updateLocalUser(u.id, { supervisorScope: toggleScopeValue(u.supervisorScope, 'shifts', x) })} /> {x}</label>)}
                      </div>
                      <div style={{ marginBottom: 6 }}>أماكن العمل:</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {WORK_LOCATION_SCOPE_OPTIONS.map(x => <label key={x} style={{ fontSize: 12 }}><input type="checkbox" checked={((u.supervisorScope?.workplaces) || []).includes(x)} onChange={() => updateLocalUser(u.id, { supervisorScope: toggleScopeValue(u.supervisorScope, 'workplaces', x) })} /> {x}</label>)}
                      </div>
                    </div>
                  ) : <span style={{ color: '#777' }}>-</span>}
                </td>}
                {!editorOnly && <td style={td}>
                  <select value={u.language || 'ar'} onChange={(e) => updateLocalUser(u.id, { language: e.target.value })} style={{ padding: '6px' }}>
                    <option value="ar">عربي</option>
                    <option value="en">English</option>
                  </select>
                </td>}
                <td style={td}>
                  {u.active ? <span style={{ color: '#27ae60' }}>نشط</span> : <span style={{ color: '#e74c3c' }}>موقوف</span>}
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {!editorOnly && <button onClick={() => saveUser(u)} style={{ ...btn, backgroundColor: '#27ae60', fontSize: '12px', padding: '5px 10px' }}>{isEnglish ? 'Save' : 'حفظ'}</button>}
                    {(!editorOnly || !((u.permissions || []).some(p => ['admin', 'manager', 'editor'].includes(p)))) ? (
                      <button onClick={() => openResetPassword(u.id, u.username)} style={{ ...btn, backgroundColor: '#f39c12', fontSize: '12px', padding: '5px 10px' }}>{isEnglish ? 'Reset Password' : 'تغيير كلمة السر'}</button>
                    ) : (
                      <span style={{ color: '#7f8c8d', fontSize: '12px' }}>{isEnglish ? 'Not available for privileged accounts' : 'غير متاح للحسابات الإدارية'}</span>
                    )}
                    {!editorOnly && u.username !== 'OPD' && (
                      <>
                        <button onClick={() => { updateLocalUser(u.id, { active: !u.active }); setTimeout(() => saveUser({ ...u, active: !u.active }), 50); }} style={{ ...btn, backgroundColor: u.active ? '#7f8c8d' : '#16a085', fontSize: '12px', padding: '5px 10px' }}>{u.active ? 'إيقاف' : 'تفعيل'}</button>
                        <button onClick={() => deleteUser(u.id, u.username)} style={{ ...btn, backgroundColor: '#e74c3c', fontSize: '12px', padding: '5px 10px' }}>حذف</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {resetTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }} dir={isEnglish ? 'ltr' : 'rtl'}>
          <div style={{ background: 'white', padding: 28, borderRadius: 10, width: '92%', maxWidth: 420, boxShadow: '0 12px 35px rgba(0,0,0,0.25)' }}>
            <h3 style={{ marginTop: 0, color: '#2c3e50' }}>{isEnglish ? 'Reset User Password' : 'إعادة تعيين كلمة مرور المستخدم'}</h3>
            <p style={{ color: '#555' }}>
              {isEnglish ? `User: ${resetTarget.username}` : `المستخدم: ${resetTarget.username}`}
            </p>
            <form onSubmit={submitResetPassword}>
              <input type="password" autoComplete="new-password" minLength={6} placeholder={isEnglish ? 'New password (minimum 6 characters)' : 'كلمة المرور الجديدة (6 أحرف على الأقل)'} value={resetPasswordValue} onChange={(e) => setResetPasswordValue(e.target.value)} style={inp} required />
              <input type="password" autoComplete="new-password" minLength={6} placeholder={isEnglish ? 'Confirm new password' : 'تأكيد كلمة المرور الجديدة'} value={resetPasswordConfirm} onChange={(e) => setResetPasswordConfirm(e.target.value)} style={inp} required />
              {resetMessage && <p style={{ color: resetMessage.includes('✅') ? '#27ae60' : '#e74c3c', fontWeight: 'bold' }}>{resetMessage}</p>}
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button type="submit" disabled={resetBusy} style={{ ...btn, backgroundColor: '#f39c12', opacity: resetBusy ? 0.7 : 1 }}>{resetBusy ? (isEnglish ? 'Saving...' : 'جاري الحفظ...') : (isEnglish ? 'Reset Password' : 'إعادة تعيين كلمة المرور')}</button>
                <button type="button" disabled={resetBusy} onClick={closeResetPassword} style={{ ...btn, backgroundColor: '#95a5a6' }}>{isEnglish ? 'Cancel' : 'إلغاء'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ====== تغيير كلمة المرور (لأي مستخدم) ======
export function ChangePasswordButton({ language = 'ar' }) {
  const isEnglish = language === 'en';
  const [open, setOpen] = useState(false);
  const [oldP, setOldP] = useState('');
  const [newP, setNewP] = useState('');
  const [confirmP, setConfirmP] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (newP.length < 6) {
      setMsg(isEnglish ? 'Password must be at least 6 characters.' : 'كلمة المرور يجب ألا تقل عن 6 أحرف.');
      return;
    }
    if (newP !== confirmP) {
      setMsg(isEnglish ? 'Passwords do not match.' : 'كلمتا المرور غير متطابقتين.');
      return;
    }
    setBusy(true);
    try {
      const res = await authFetch(`${API_URL}/auth/change-password`, { method: 'POST', body: JSON.stringify({ oldPassword: oldP, newPassword: newP }) });
      const data = await res.json();
      if (res.ok) {
        if (data.token) localStorage.setItem('opd_token', data.token);
        setMsg(isEnglish ? 'Changed successfully. Your current session was refreshed. ✅' : 'تم التغيير وتحديث جلستك الحالية. ✅');
        setOldP('');
        setNewP('');
        setConfirmP('');
        setTimeout(() => setOpen(false), 1200);
      } else setMsg(data.error || (isEnglish ? 'Unable to change password' : 'تعذر التغيير'));
    } catch { setMsg(isEnglish ? 'Cannot connect to server' : 'تعذر الاتصال بالخادم'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} style={{ backgroundColor: '#34495e', color: 'white', padding: '8px 14px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
        {isEnglish ? 'Password' : 'كلمة المرور'}
      </button>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} dir={isEnglish ? 'ltr' : 'rtl'}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '10px', width: '90%', maxWidth: '380px' }}>
            <h3 style={{ marginTop: 0, color: '#2c3e50' }}>{isEnglish ? 'Change Password' : 'تغيير كلمة المرور'}</h3>
            <form onSubmit={submit}>
              <input type="password" placeholder={isEnglish ? 'Current Password' : 'كلمة المرور الحالية'} value={oldP} onChange={(e) => setOldP(e.target.value)} style={inp} required />
              <input type="password" autoComplete="new-password" minLength={6} placeholder={isEnglish ? 'New Password (minimum 6 characters)' : 'كلمة المرور الجديدة (6 أحرف على الأقل)'} value={newP} onChange={(e) => setNewP(e.target.value)} style={inp} required />
              <input type="password" autoComplete="new-password" minLength={6} placeholder={isEnglish ? 'Confirm New Password' : 'تأكيد كلمة المرور الجديدة'} value={confirmP} onChange={(e) => setConfirmP(e.target.value)} style={inp} required />
              {msg && <p style={{ color: msg.includes('✅') ? '#27ae60' : '#e74c3c', fontWeight: 'bold' }}>{msg}</p>}
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button type="submit" disabled={busy} style={{ ...btn, backgroundColor: '#27ae60', opacity: busy ? 0.7 : 1 }}>{busy ? (isEnglish ? 'Saving...' : 'جاري الحفظ...') : (isEnglish ? 'Save' : 'حفظ')}</button>
                <button type="button" disabled={busy} onClick={() => { setOpen(false); setOldP(''); setNewP(''); setConfirmP(''); setMsg(''); }} style={{ ...btn, backgroundColor: '#95a5a6' }}>{isEnglish ? 'Cancel' : 'إلغاء'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
